import { Link2Off } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/atoms/alert-dialog';

/**
 * Confirmation for dismissing a half-finished Stripe Connect onboarding.
 *
 * This replaces a bare `window.confirm` — same guard, but it can actually
 * explain what dismissing does (drops the incomplete account so setup can be
 * restarted from scratch) and it disables itself while the mutation is in
 * flight instead of letting a double-click fire twice.
 */
export function StripeDismissConfirmDialog({
  trigger,
  onConfirm,
  isPending,
}: {
  trigger: ReactNode;
  onConfirm: () => void;
  isPending?: boolean;
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Link2Off className="size-4" />
            Dismiss this Stripe connection?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                Onboarding for this Stripe account was never completed — no charges or payouts are
                enabled on it.
              </p>
              <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                <li>The incomplete account is unlinked from Notifi.</li>
                <li>Nothing is deleted inside Stripe.</li>
                <li>You can start Stripe setup again at any time.</li>
              </ul>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction variant="destructive" disabled={isPending} onClick={onConfirm}>
            {isPending ? 'Dismissing…' : 'Dismiss connection'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
